import { useDispatch } from 'react-redux';
import axiosInstanceQueries from '../queriesAxios';
import { useMutation, UseMutationResult, useQuery, useQueryClient } from '@tanstack/react-query';
import app from 'store/app/app';

export interface CustomStatus {
  statusType: 'First' | 'Second' | 'Third';
  visible: boolean;
  active: boolean;
  icon: 'Thumbup' | 'Thumbdown' | 'Hidden';
  id: string;
  color: 'Green' | 'Red' | 'Blue';
  title: string;
}

export interface UpdateCustomStatus {
  statusType?: 'First' | 'Second' | 'Third';
  visible?: boolean;
  active?: boolean;
  icon?: 'Thumbup' | 'Thumbdown' | 'Hidden';
  color?: 'Green' | 'Red' | 'Blue';
  title?: string;
}

export interface InspectionCustomStatuses {
  currentStatuses: Record<string, CustomStatus>;
  allStatuses: Record<string, CustomStatus>;
}

async function fetchCustomStatuses(): Promise<CustomStatus[]> {
  return axiosInstanceQueries.get('/statuses').then((response) => response.data);
}

async function updateCustomStatus(updatedStatuses: UpdateCustomStatus[]): Promise<void> {
  const requestConfig = {
    method: 'patch' as const,
    url: '/statuses',
    data: { updatedStatuses },
  };
  await axiosInstanceQueries.request(requestConfig);
}

async function fetchInspectionCustomStatuses(inspectionId: string): Promise<InspectionCustomStatuses> {
  return await axiosInstanceQueries.get(`/statuses/${inspectionId}`).then((response) => response.data);
}

export const useCustomStatuses = () => {
  const queryInfo = useQuery({
    queryKey: ['customStatuses'],
    queryFn: () => fetchCustomStatuses(),
    staleTime: 1000 * 60 * 5,
  });
  return {
    ...queryInfo,
    customStatusesList: queryInfo.data || [],
  };
};

export const useUpdateCustomStatus = (): UseMutationResult<
  void,
  Error,
  { updatedCustomStatuses: UpdateCustomStatus[] }
> => {
  const queryClient = useQueryClient();
  const dispatch = useDispatch();

  return useMutation({
    mutationFn: ({ updatedCustomStatuses }) => updateCustomStatus(updatedCustomStatuses),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['customStatuses'] });
      queryClient.invalidateQueries({ queryKey: ['inspectionCustomStatuses'] });
      dispatch(app.actions.setSnackMessage({ message: 'Status updated successfully!', type: 'success' }));
    },
    onError: (error) => {
      dispatch(app.actions.setSnackMessage({ message: `Error updating status: ${error.message}`, type: 'error' }));
    },
  });
};

export const useInspectionCustomStatuses = (inspectionId: string) => {
  const queryClient = useQueryClient();

  const queryInfo = useQuery({
    queryKey: ['inspectionCustomStatuses', inspectionId],
    queryFn: () => (inspectionId ? fetchInspectionCustomStatuses(inspectionId) : Promise.resolve(null)),
    enabled: !!inspectionId, // query only runs when inspectionId is defined
    staleTime: 1000 * 60 * 5,
  });

  const refetchStatuses = () => {
    queryClient.refetchQueries({ queryKey: ['inspectionCustomStatuses', inspectionId] });
  };

  return {
    ...queryInfo,
    customStatuses: queryInfo.data,
    refetchStatuses,
  };
};
